import { DataTable } from '@/components'
import { cn } from '@/lib/utils'
import type { ResultOf } from 'gql.tada'
import { format } from '@/utils/date-fns/format'
import { createColumnHelper } from '@tanstack/react-table'
import type { SortingState } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { Link } from 'react-router'
import type { ALL_DRIVER_FORECASTS_QUERY } from '../queries'
import type { ForecastUser } from './remaining-driving-time'
import { formatMinutes, remainingLevel } from './remaining-driving-time'

type ForecastNode = ResultOf<
  typeof ALL_DRIVER_FORECASTS_QUERY
>['allForecasts'][number]

export type ForecastRow = ForecastNode & { id: string; user: ForecastUser }

type MinutesKey =
  | 'remainingDaily'
  | 'remainingWeekly'
  | 'remainingTwoWeekly'
  | 'remainingWeeklyWorkingTime'
  | 'totalWeeklyWorkingTime'

const DEFAULT_SORT: SortingState = [{ id: 'remainingDaily', desc: false }]

const levelClassNames = {
  unknown: 'text-stone-400',
  exceeded: 'text-rose-700 before:bg-rose-500',
  low: 'text-amber-700 before:bg-amber-500',
  ok: 'text-stone-900',
}

const RemainingCell: React.FC<{ minutes: number | null }> = ({ minutes }) => {
  const level = remainingLevel(minutes)

  return (
    <span
      className={cn(
        'text-sm font-semibold tabular-nums whitespace-nowrap',
        (level === 'exceeded' || level === 'low') &&
          "before:content-[''] before:inline-block before:size-2 before:mr-2 before:mb-px before:rounded-full",
        levelClassNames[level]
      )}
    >
      {formatMinutes(minutes)}
    </span>
  )
}

export function parseOrderBy(orderBy: string): SortingState {
  if (!orderBy) return DEFAULT_SORT

  const desc = orderBy.startsWith('-')
  return [{ id: desc ? orderBy.slice(1) : orderBy, desc }]
}

export function toOrderBy(sorting: SortingState): string {
  const next = sorting[0]
  if (!next) return ''

  return next.desc ? `-${next.id}` : next.id
}

interface Props {
  forecasts: ForecastRow[]
  loading: boolean
  orderBy: string
  onOrderByChange(orderBy: string): void
}

export const RemainingDrivingTimeTable: React.FC<Props> = ({
  forecasts,
  loading,
  orderBy,
  onOrderByChange,
}) => {
  const translations = useTranslate({
    employee: 'common.employee',
    driving: 'activities.remaining-driving-time.driving',
    daily: 'common.daily',
    weekly: 'common.weekly',
    twoWeekly: 'activities.remaining-driving-time.two-weekly',
    workTime: 'activities.remaining-driving-time.work-time',
    remaining: 'activities.remaining-driving-time.remaining',
    total: 'activities.remaining-driving-time.total',
    latestUpdate: 'activities.last-update',
  })
  const columns = useMemo(() => {
    const column = createColumnHelper<ForecastRow>()

    const minutesColumn = (
      key: MinutesKey,
      header: string,
      highlight: boolean
    ) =>
      column.accessor(row => row[key] ?? undefined, {
        id: key,
        header,
        enableSorting: true,
        sortUndefined: 'last',
        meta: { narrow: true, end: true },
        cell: info => {
          const minutes = info.row.original[key]
          if (highlight) return <RemainingCell minutes={minutes} />

          return (
            <span className="text-sm tabular-nums text-stone-700">
              {formatMinutes(minutes)}
            </span>
          )
        },
      })

    return [
      column.accessor(row => row.user.fullName, {
        id: 'user',
        header: translations.employee,
        enableSorting: true,
        sortingFn: (a, b) =>
          a.original.user.fullName.localeCompare(b.original.user.fullName),
        cell: info => (
          <Link
            className="font-medium text-stone-900 hover:text-secondary"
            to={`/users/${info.row.original.user.id}`}
          >
            {info.getValue()}
          </Link>
        ),
      }),
      column.group({
        id: 'driving',
        header: translations.driving,
        meta: { center: true },
        columns: [
          minutesColumn('remainingDaily', translations.daily, true),
          minutesColumn('remainingWeekly', translations.weekly, true),
          minutesColumn('remainingTwoWeekly', translations.twoWeekly, true),
        ],
      }),
      column.group({
        id: 'workTime',
        header: translations.workTime,
        meta: { center: true },
        columns: [
          minutesColumn(
            'remainingWeeklyWorkingTime',
            translations.remaining,
            true
          ),
          minutesColumn('totalWeeklyWorkingTime', translations.total, false),
        ],
      }),
      column.accessor(
        row =>
          row.latestUpdate ? new Date(row.latestUpdate).getTime() : undefined,
        {
          id: 'latestUpdate',
          header: translations.latestUpdate,
          enableSorting: true,
          sortUndefined: 'last',
          meta: { narrow: true, end: true },
          cell: info => {
            const latestUpdate = info.row.original.latestUpdate

            return (
              <span className="text-sm whitespace-nowrap text-stone-500">
                {latestUpdate
                  ? format(new Date(latestUpdate), 'dd. MMM yyyy HH:mm')
                  : '–'}
              </span>
            )
          },
        }
      ),
    ]
  }, [
    translations.daily,
    translations.driving,
    translations.employee,
    translations.latestUpdate,
    translations.remaining,
    translations.total,
    translations.twoWeekly,
    translations.weekly,
    translations.workTime,
  ])

  const sorting = useMemo(() => parseOrderBy(orderBy), [orderBy])

  return (
    <DataTable
      columns={columns}
      data={forecasts}
      loading={loading}
      pagination={{ pageSize: 50 }}
      sorting={sorting}
      onSortingChange={next => onOrderByChange(toOrderBy(next))}
      inCard
    />
  )
}
